import * as React from 'react';
import { isEqual } from 'owa-datetime';
import { deleteTodo, updateTodoSubject, updateTodoDueDate } from 'owa-todo-actions';
import { isTaskComplete } from 'owa-todo-utils';
import { getAsset, getProject, setDueDate, setIsComplete, setTaskId } from 'owa-timestream-store';
import type { ProjectId, AssetId } from 'owa-timestream-types';
import { todoCache, createFolderIdDescriptor } from '../todoCache';
import { spacesTodoActionTelemetryData } from '../constants';
import createTask from '../utils/createTask';
import isCreatingTask from '../utils/isCreatingTask';

export default function useTaskDueDateSync(
    projectId: ProjectId,
    assetId: AssetId,
    assetTitle: string | undefined | null,
    preventFullControlOverTask?: boolean
) {
    const { tasksParentFolderId, isShared } = getProject(projectId);
    const { dueDate, isComplete, taskId } = getAsset(projectId, assetId);
    const taskShouldExist = !isShared && !!dueDate;
    const task =
        !!tasksParentFolderId && !!taskId && !isShared
            ? todoCache.getTaskForLock(taskId, createFolderIdDescriptor(tasksParentFolderId))
            : undefined;

    // Create a task that should exist
    React.useEffect(
        function createTaskThatShouldExist() {
            if (taskShouldExist && !taskId && assetTitle) {
                createTask(projectId, assetId, tasksParentFolderId, assetTitle, dueDate);
            }
        },
        [projectId, assetId, taskShouldExist, taskId, dueDate, assetTitle, tasksParentFolderId]
    );

    // Patch asset with task due date and completion
    React.useEffect(
        function updateAssetIfTaskIsUpdated() {
            if (!task) {
                return;
            }

            if (task.DueDateTime && dueDate && !isEqual(task.DueDateTime, dueDate)) {
                setDueDate(projectId, assetId, task.DueDateTime);
            }

            const isSourceTaskComplete = isTaskComplete(task.Status);
            if (isSourceTaskComplete != !!isComplete) {
                setIsComplete(projectId, assetId, isSourceTaskComplete);
            }
        },
        [projectId, assetId, task?.DueDateTime, dueDate, isComplete]
    );

    // Patch task with updated asset title
    React.useEffect(
        function updateTaskSubjectWithAssetTitle() {
            if (!task) {
                return;
            }

            if (
                !preventFullControlOverTask &&
                taskId &&
                !isCreatingTask(taskId) &&
                assetTitle &&
                assetTitle !== task.Subject
            ) {
                updateTodoSubject(taskId, assetTitle, spacesTodoActionTelemetryData);
            }
        },
        [taskId, assetTitle, task?.Subject, preventFullControlOverTask]
    );

    // Patch task with due date if there is not task due date
    React.useEffect(
        function updateTaskDueDate() {
            if (!task) {
                return;
            }

            if (
                taskId &&
                !isCreatingTask(taskId) &&
                !task.DueDateTime &&
                dueDate &&
                !preventFullControlOverTask
            ) {
                updateTodoDueDate(taskId, dueDate, spacesTodoActionTelemetryData);
            }
        },
        [taskId, task?.DueDateTime, dueDate]
    );

    // Patch asset with new due date only if the asset doesn't have full control
    React.useEffect(
        function updateAssetDueDate() {
            if (!task) {
                return;
            }

            if (preventFullControlOverTask) {
                if (taskId && !isCreatingTask(taskId) && task?.DueDateTime && !dueDate) {
                    setDueDate(projectId, assetId, task.DueDateTime);
                } else if (taskId && !isCreatingTask(taskId) && !task?.DueDateTime && dueDate) {
                    setDueDate(projectId, assetId, null);
                }
            }
        },
        [taskId, preventFullControlOverTask, task?.DueDateTime, dueDate, projectId, assetId]
    );

    // Remove task when the date is removed
    React.useEffect(
        function removeTaskOnDateRemoved() {
            if (!task) {
                return;
            }

            if (!preventFullControlOverTask && !dueDate && taskId && !isCreatingTask(taskId)) {
                deleteTodo(taskId, spacesTodoActionTelemetryData);
                setTaskId(projectId, assetId, '');
            } else if (
                preventFullControlOverTask &&
                !dueDate &&
                task.DueDateTime &&
                taskId &&
                !isCreatingTask(taskId)
            ) {
                updateTodoDueDate(taskId, null /* dueDate */, spacesTodoActionTelemetryData);
            }
        },
        [projectId, assetId, dueDate, taskId, preventFullControlOverTask]
    );

    return {
        dueDate: task?.DueDateTime || dueDate,
        isComplete: !!task?.Status ? isTaskComplete(task.Status) : isComplete || false,
    };
}
